import { Table } from "react-bootstrap";
import TeacherTableRow from "./TeacherTableRow";
import TablePagination from "../../TeacherAdmin/pagination/TablePagination";
import TableSkeleton from "../../common/TableSkeleton";
import TableError from "../../common/TableError";
import "../../../../common/styles/table.css";

/**
 * @typedef {import("../../../../hooks/organization/report/useGetSubSchoolReportById").TeacherReportItem} TeacherReportItem
 */

/**
 * TeacherTable component displays a list of teachers with their performance data
 * @param {object} props - Component props
 * @param {TeacherReportItem[]} [props.teachers] - Array of teacher data
 * @param {boolean} [props.loading] - Loading state
 * @param {string} [props.error] - Error message
 * @param {object} [props.metaData] - Pagination metadata
 * @param {number} [props.currentPage] - Current page number
 * @param {Function} [props.setCurrentPage] - Function to update current page
 * @param {Function} [props.handleRefresh] - Function to refresh data
 * @param {Function} [props.onTeacherClick] - Callback when a teacher row is clicked
 * @param {boolean} [props.isInitialLoad] - Whether this is the initial load
 * @returns {JSX.Element}
 */
const TeacherTable = ({
  teachers = [],
  loading = false,
  error = "",
  metaData,
  currentPage = 1,
  setCurrentPage,
  handleRefresh,
  onTeacherClick,
  isInitialLoad = false,
}) => {
  const totalPages = metaData?.last_page || 1;

  // Show skeleton only on initial load
  if (loading && isInitialLoad) {
    return <TableSkeleton rows={5} columns={7} />;
  }

  // Show error state
  if (error) {
    return (
      <TableError
        error={error}
        handleRefresh={handleRefresh}
        loading={loading}
      />
    );
  }

  // Show empty state
  if (!teachers || teachers.length === 0) {
    return (
      <div className="alert alert-info mt-3" role="alert">
        No teachers found.
      </div>
    );
  }

  return (
    <div className="custom-table-container mt-3">
      <div className="custom-table-wrapper">
        <Table hover className="custom-table">
          <thead className="custom-table-head">
            <tr>
              <th>Teacher Name</th>
              <th>Class(es)</th>
              <th>Students Assigned</th>
              <th>Avg Student Progress</th>
              <th>Pass Rate</th>
              <th>Lessons Assigned</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {teachers.map((teacher) => (
              <TeacherTableRow
                key={teacher.teacher_id}
                teacher={teacher}
                onClick={onTeacherClick}
              />
            ))}
          </tbody>
        </Table>
      </div>

      <TablePagination
        totalPage={totalPages}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        disabled={loading}
      />
    </div>
  );
};

export default TeacherTable;
